import React, { useEffect, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Row,
  Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { updateDeliveryPatternsData } from "../../services/delivery-patterns/deliveryPatternsService";
import {
  doGetDeliveryPatterns,
  setDeliveryPatternsError,
  setDeliveryPatternsFilter,
  updateItemDeliveryPatterns,
} from "../../stores/actions";
import {
  selectDeliveryPatternsData,
  selectDeliveryPatternsError,
  selectDeliveryPatternsFilter,
  selectDeliveryPatternsModified,
} from "../../stores/reducers/deliveryPatternsReducer";
import DeliveryPatternsFilter from "./components/DeliveryPatternsFilter";
import DeliveryPatternsForm from "./components/DeliveryPatternsForm";
import DeliveryPatternsTable from "./components/DeliveryPatternsTable";
import "./index.scss";

export default function DeliveryPatterns() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // state
  const [dataError, setDataError] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showAlert, setShowAlert] = useState({
    status: false,
    message: "",
    key: "",
  });

  //redux state
  const deliveryPatternsData = useSelector(selectDeliveryPatternsData);
  const deliveryPatternsModified = useSelector(selectDeliveryPatternsModified);
  const filter = useSelector(selectDeliveryPatternsFilter);
  const deliveryPatternsError = useSelector(selectDeliveryPatternsError);

  //get data
  useEffect(() => {
    dispatch(doGetDeliveryPatterns(filter));
  }, [dispatch, filter]);

  //remove filter
  useEffect(() => {
    return () => {
      dispatch(
        setDeliveryPatternsFilter({
          code: undefined,
          name: undefined,
        })
      );
    };
  }, [dispatch]);

  const handleChangeFilter = (newFilter) => {
    handleCloseAlert();
    dispatch(setDeliveryPatternsFilter(newFilter));
  };

  const handleRowChange = (newRow) => {
    dispatch(updateItemDeliveryPatterns(newRow));
  };

  const handleCloseAlert = () => {
    setShowAlert({ status: false, message: "", key: "" });
  };

  const handleShowAlert = (message, key) => {
    setShowAlert({ status: true, message, key });
  };

  const handleSubmit = async () => {
    const itemErrors = [];
    Object.values(deliveryPatternsModified).forEach((item) => {
      // check required
      if (item.patternnm.length === 0) {
        itemErrors.push(item);
      }
    });

    if (itemErrors.length > 0) {
      // show error message
      setDataError(itemErrors);
      handleShowAlert(
        t("delivery_patterns.message.warn_invalid_data"),
        "danger"
      );
    } else {
      // reset error
      handleCloseAlert();
      setDataError([]);

      if (Object.values(deliveryPatternsModified).length === 0) {
        handleShowAlert(
          t("delivery_patterns.message.no_updated_data"),
          "warning"
        );
      } else {
        //compose data
        const submitData = Object.values(deliveryPatternsModified).map(
          (item) => {
            return item;
          }
        );

        // submit modified data to server
        try {
          dispatch(setDeliveryPatternsError([]));
          setUpdateLoading(true);
          await updateDeliveryPatternsData(submitData);
          dispatch(doGetDeliveryPatterns(filter));
          setUpdateLoading(false);
          handleShowAlert(
            t("delivery_patterns.message.update_successfully"),
            "success"
          );
        } catch (e) {
          setUpdateLoading(false);
          if (e.response && e.response.data) {
            handleShowAlert(
              t("delivery_patterns.message.update_failed"),
              "danger"
            );

            //show error duplicated
            dispatch(setDeliveryPatternsError(e.response.data));
          }
        }
      }
    }
  };

  // scroll to first error row
  useEffect(() => {
    if (showAlert.status) {
      if (deliveryPatternsError.length > 0) {
        setTimeout(() => {
          document
            .getElementById("row-" + deliveryPatternsError[0]["data"].patterncd)
            .scrollIntoView({
              behavior: "auto",
              block: "center",
              inline: "center",
            });
        }, 300);
      } else if (dataError.length > 0) {
        setTimeout(() => {
          document
            .getElementById("row-" + dataError[0].patterncd)
            .scrollIntoView({
              behavior: "auto",
              block: "center",
              inline: "center",
            });
        }, 300);
      }
    }
  }, [dataError, deliveryPatternsError, showAlert.status]);

  const handleCloseForm = () => {
    setShowForm(false);
  };

  const handleOpenForm = () => {
    setShowForm(true);
  };

  const handleSubmitFormSuccessfully = () => {
    handleShowAlert(t("ordertype.message.add_successfully"), "success");
    dispatch(doGetDeliveryPatterns(filter));
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <Breadcrumb>
            <Breadcrumb.Item active>{t("system.label.home")}</Breadcrumb.Item>
            <Breadcrumb.Item active>
              {t("delivery_patterns.label.headtitle")}
            </Breadcrumb.Item>
          </Breadcrumb>
          <Card>
            <Card.Header
              as="h2"
              style={{ textAlign: "center", color: "#ef8412" }}
            >
              {t("delivery_patterns.label.headtitle")}
            </Card.Header>

            <Card.Body>
              <DeliveryPatternsFilter
                filter={filter}
                onChange={handleChangeFilter}
              />

              <Row className="my-3">
                <Col sm={10}>
                  {showAlert.status && (
                    <Alert
                      key={showAlert.key}
                      variant={showAlert.key}
                      onClose={handleCloseAlert}
                      dismissible
                    >
                      {showAlert.message}
                    </Alert>
                  )}
                </Col>
                <Col sm={2} className="text-end">
                  <Button
                    style={{ minWidth: "150px" }}
                    onClick={handleOpenForm}
                  >
                    <FaPlus />
                    &nbsp;{t("delivery_patterns.label.sign_up")}
                  </Button>
                </Col>
              </Row>
              <DeliveryPatternsTable
                data={deliveryPatternsData}
                onRowChange={handleRowChange}
              />
              <div className="text-center mt-5 mb-3">
                <Button
                  variant="primary"
                  style={{ minWidth: "150px" }}
                  onClick={handleSubmit}
                  disabled={updateLoading}
                >
                  {updateLoading && (
                    <Spinner
                      animation="border"
                      size="sm"
                      variant="light"
                      className="me-1"
                    />
                  )}
                  <span>{t("system.label.update")}</span>
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <DeliveryPatternsForm
        open={showForm}
        onClose={handleCloseForm}
        onSubmitSuccessfully={handleSubmitFormSuccessfully}
      />
    </>
  );
}
